import { ReactNode, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  GRAY_SECONDARY,
  ORANGE,
  ORANGE_SECONDARY,
  PRIMARY,
  RED,
  SECONDARY
} from 'constants/colors';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const StyledDialog = styled.div`
  min-width: 360px;
  padding: 10px;

  .MuiDialogTitle-root {
    padding: 10px;
    border-bottom: 1px solid ${GRAY_SECONDARY};
    margin-bottom: 10px;
  }

  .MuiDialogContent-root {
    padding: 10px;
    margin-bottom: 20px;
    font-size: 16px;
    font-family: 'SourceSansPro-Regular';
    white-space: pre-line;

    p {
      margin: 0;
    }
  }

  .MuiDialogTitle-root {
    font-size: 18px;
  }

  &.info {
    .MuiDialogTitle-root {
      border-color: ${PRIMARY};
      color: ${SECONDARY};
    }
  }
  &.warning {
    .MuiDialogTitle-root {
      border-color: ${ORANGE};
      color: ${ORANGE_SECONDARY};
    }
  }
  &.error {
    .MuiDialogTitle-root {
      border-color: ${RED};
      color: ${RED};
    }
  }
`;

type Props = {
  title?: string | null;
  type?: 'error' | 'warning' | 'info';
  handleAction: (isConfirmed: boolean) => void;
  handleSecondAction?: (isConfirmed: boolean) => void;
  onClose?: (isConfirmed: boolean) => void;
  enableSecondAction?: boolean;
  cancelBtnText?: string;
  confirmBtnText?: string;
  confirmSecondBtnText?: string;
  children: ReactNode | ReactNode[];
  disableConfirm?: boolean;
};

export default function ConfirmationDialog(props: Props) {
  const { t } = useTranslation('common');
  const {
    title,
    handleAction,
    handleSecondAction,
    onClose,
    cancelBtnText = t('cancel'),
    confirmBtnText = t('ok'),
    children,
    type,
    disableConfirm,
    enableSecondAction = false,
    confirmSecondBtnText = t('ok')
  } = props;
  const [isOpen, setIsOpen] = useState(true);

  const closeWithConfirmation = async (isConfirmed, action) => {
    const shouldClose = await action(isConfirmed); // Await the action to determine whether to close
    if (shouldClose) {
      setIsOpen(false);
    }
  };

  return (
    <Dialog
      aria-labelledby="confirmation-dialog"
      open={isOpen}
      sx={{ '& .MuiDialog-paper': { maxWidth: '800px' } }}
    >
      <StyledDialog className={classNames(type)}>
        {title && <DialogTitle id="confirmation-dialog">{title}</DialogTitle>}
        {children && <DialogContent>{children}</DialogContent>}
        <DialogActions>
          <Button
            onClick={() => closeWithConfirmation(false, onClose)}
            color="secondary"
            variant="contained"
          >
            {cancelBtnText}
          </Button>
          <Button
            onClick={() => closeWithConfirmation(true, handleAction)}
            color="primary"
            variant="contained"
            disabled={disableConfirm}
          >
            {confirmBtnText}
          </Button>
          {enableSecondAction && (
            <Button
              onClick={() => closeWithConfirmation(true, handleSecondAction)}
              color="primary"
              variant="contained"
              disabled={disableConfirm}
            >
              {confirmSecondBtnText}
            </Button>
          )}
        </DialogActions>
      </StyledDialog>
    </Dialog>
  );
}
